import { forwardRef, memo } from "react"

import { MouseClickFocusRemover } from "../../utils"

import {
  ButtonIcon,
  StyledAnchor,
  StyledButton,
  StyledLink,
  StyledSpinner,
  ChildrenContainer,
} from "./styles"
import { ButtonProps, ButtonStyledAnchorProps, ButtonStyledLinkProps } from "./types"

export * from "./types"

export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        children,
        loading,
        iconPosition,
        iconVerticalAlignment,
        svgIcon,
        svgIconTransform,
        size,
        variant = "primary",
        emptyWhileLoading,
        horizontalAlignment,
        className,
        ...props
      },
      ref,
    ) => (
      <StyledButton
        aria-label={children?.toString()}
        {...props}
        size={size}
        variant={variant}
        loading={loading}
        ref={ref}
        className={`otto-button ${className}`}
      >
        <MouseClickFocusRemover aria-hidden="true">
          <ChildrenContainer
            $horizontalAlignment={horizontalAlignment}
            $emptyWhileLoading={emptyWhileLoading}
            $loading={loading}
          >
            {children}
          </ChildrenContainer>
          {loading && (
            <StyledSpinner
              buttonSize={size}
              size={size === "large" ? "small" : "xSmall"}
              color={
                variant === "primary" || variant === "destructiveSolid" ? "white" : "grey"
              }
              isCentered={!children || emptyWhileLoading}
            />
          )}
          {svgIcon && !loading && (
            <ButtonIcon
              buttonSize={size}
              iconPosition={iconPosition}
              iconVerticalAlignment={iconVerticalAlignment}
              svgIcon={svgIcon}
              transform={svgIconTransform}
            />
          )}
        </MouseClickFocusRemover>
      </StyledButton>
    ),
  ),
)

/**
 * Please do not use these two combinations:  \
 * `size="large" variant="minor"`  \
 * `size="small" variant="destructive"`
 * ___
 * For more information see https://design.izettle.net/components/buttons/web
 */
export const ButtonStyledLink = memo(
  forwardRef<HTMLAnchorElement, ButtonStyledLinkProps>(
    (
      { children, iconPosition, svgIcon, svgIconTransform, size, className, ...props },
      forwardedRef,
    ) => (
      <StyledLink
        {...props}
        size={size}
        innerRef={forwardedRef}
        className={`otto-button ${className}`}
      >
        <MouseClickFocusRemover>
          {children}
          {svgIcon && (
            <ButtonIcon
              buttonSize={size}
              iconPosition={iconPosition}
              svgIcon={svgIcon}
              transform={svgIconTransform}
            />
          )}
        </MouseClickFocusRemover>
      </StyledLink>
    ),
  ),
)

/**
 * Please do not use these two combinations:  \
 * `size="large" variant="minor"`  \
 * `size="small" variant="destructive"`
 * ___
 * For more information see https://design.izettle.net/components/buttons/web
 */
export const ButtonStyledAnchor = memo(
  forwardRef<HTMLAnchorElement, ButtonStyledAnchorProps>(
    (
      { children, iconPosition, svgIcon, svgIconTransform, size, className, ...props },
      ref,
    ) => (
      <StyledAnchor
        {...props}
        size={size}
        ref={ref}
        className={`otto-button ${className}`}
      >
        <MouseClickFocusRemover>
          {children}
          {svgIcon && (
            <ButtonIcon
              buttonSize={size}
              iconPosition={iconPosition}
              svgIcon={svgIcon}
              transform={svgIconTransform}
            />
          )}
        </MouseClickFocusRemover>
      </StyledAnchor>
    ),
  ),
)
